/**
 * Navigation Context Hook
 * 
 * Manages smart back navigation across the app with FULL browser back button support.
 * Uses sessionStorage to persist navigation history so browser back/forward works correctly.
 * 
 * Usage:
 * 
 * 1. When navigating TO a document:
 *    const { navigateWithReturn } = useNavigationContext();
 *    navigateWithReturn(`/invoices/${id}`);
 * 
 * 2. When going BACK from a document:
 *    const { goBack } = useNavigationContext();
 *    <Button onClick={goBack}>Back</Button>
 * 
 * 3. The browser's back button will also work correctly!
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';

// Session storage key for navigation history
const NAV_HISTORY_KEY = 'akountfly_nav_history';
const MAX_HISTORY_SIZE = 50;

/**
 * Get navigation history from session storage
 */
const getNavHistory = () => {
  try {
    const history = sessionStorage.getItem(NAV_HISTORY_KEY);
    return history ? JSON.parse(history) : [];
  } catch {
    return [];
  }
};

/**
 * Save navigation history to session storage
 */
const setNavHistory = (history) => {
  try {
    // Limit history size
    const trimmed = history.slice(-MAX_HISTORY_SIZE);
    sessionStorage.setItem(NAV_HISTORY_KEY, JSON.stringify(trimmed));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Add a path to navigation history
 */
const pushToHistory = (path, label) => {
  const history = getNavHistory();
  const lastEntry = history[history.length - 1];
  
  // Don't add duplicate consecutive entries
  if (lastEntry?.path !== path) {
    history.push({ path, label, timestamp: Date.now() });
    setNavHistory(history);
  }
};

/**
 * Get the previous path from history
 */
const getPreviousFromHistory = (currentPath) => {
  const history = getNavHistory();
  
  // Find the last entry that isn't the current path
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].path !== currentPath) {
      return history[i];
    }
  }
  return null;
};

export const useNavigationContext = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastPathRef = useRef(null);
  
  // Track navigation history on every location change
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    
    // Only record if path actually changed (not just state updates)
    if (lastPathRef.current !== currentPath) {
      const label = getPageLabel(location.pathname);
      pushToHistory(currentPath, label);
      lastPathRef.current = currentPath;
    }
  }, [location.pathname, location.search]);
  
  /**
   * Navigate to a route while preserving the current location as return path
   * @param {string} to - The route to navigate to
   * @param {object} options - Additional navigate options
   */
  const navigateWithReturn = useCallback((to, options = {}) => {
    const returnUrl = location.pathname + location.search;
    navigate(to, {
      ...options,
      state: {
        ...options.state,
        returnUrl,
        returnLabel: getPageLabel(location.pathname)
      }
    });
  }, [navigate, location]);

  /**
   * Go back to the previous page - always uses browser history (like pressing browser back).
   * Falls back to a default route only when there's no browser history (e.g., direct URL access).
   * @param {string} defaultFallback - Default route if no browser history exists
   */
  const goBack = useCallback((defaultFallback = '/dashboard') => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(defaultFallback);
    }
  }, [navigate]);

  /**
   * Get the return URL from location state or history
   */
  const getReturnUrl = useCallback(() => {
    // First check state
    if (location.state?.returnUrl) {
      return location.state.returnUrl;
    }
    // Then check history
    const currentPath = location.pathname + location.search;
    const previous = getPreviousFromHistory(currentPath);
    return previous?.path || null;
  }, [location.state, location.pathname, location.search]);

  /**
   * Get a human-readable label for the return location
   */
  const getReturnLabel = useCallback(() => {
    // First check state
    if (location.state?.returnLabel) {
      return location.state.returnLabel;
    }
    // Then check history
    const currentPath = location.pathname + location.search;
    const previous = getPreviousFromHistory(currentPath);
    return previous?.label || 'Back';
  }, [location.state, location.pathname, location.search]);

  /**
   * Check if there's a valid return destination
   */
  const hasReturnUrl = useCallback(() => {
    if (location.state?.returnUrl) {
      return true;
    }
    const currentPath = location.pathname + location.search;
    return !!getPreviousFromHistory(currentPath);
  }, [location.state, location.pathname, location.search]);

  /**
   * Clear navigation history (useful for logout)
   */
  const clearHistory = useCallback(() => {
    sessionStorage.removeItem(NAV_HISTORY_KEY);
  }, []);

  return {
    navigateWithReturn,
    goBack,
    getReturnUrl,
    getReturnLabel,
    hasReturnUrl,
    clearHistory,
    currentPath: location.pathname
  };
};

/**
 * Get a human-readable label for a route path
 */
function getPageLabel(pathname) {
  const labels = {
    '/reports': 'Reports',
    '/reports/general-ledger': 'General Ledger',
    '/insights': 'Insights',
    '/advanced-view': 'Advanced View',
    '/dashboard': 'Dashboard',
    '/invoices': 'Get Paid',
    '/invoices/new': 'New Invoice',
    '/bills': 'Pay Bills',
    '/bills/new': 'New Bill',
    '/banks': 'My Banks',
    '/settings': 'Settings',
    '/reconciliation': 'AI Reconcile',
    '/products-services': 'Products & Services',
    '/chat': 'AkountFly AI',
    '/statement-upload': 'Bank Transactions',
    '/transactions/review': 'Transaction Review',
    '/deferred-revenue': 'Deferred Revenue',
    '/fixed-assets': 'Fixed Assets',
  };

  // Check exact matches first
  if (labels[pathname]) {
    return labels[pathname];
  }

  // Check for specific patterns
  if (pathname.match(/^\/invoices\/[^/]+\/edit$/)) return 'Edit Invoice';
  if (pathname.match(/^\/invoices\/[^/]+$/)) return 'Invoice Details';
  if (pathname.match(/^\/bills\/[^/]+\/edit$/)) return 'Edit Bill';
  if (pathname.match(/^\/bills\/[^/]+$/)) return 'Bill Details';

  // Check for partial matches
  for (const [path, label] of Object.entries(labels)) {
    if (pathname.startsWith(path)) {
      return label;
    }
  }

  // Check for report-specific paths
  if (pathname.includes('general-ledger')) return 'General Ledger';
  if (pathname.includes('profit-loss')) return 'Profit & Loss';
  if (pathname.includes('balance-sheet')) return 'Balance Sheet';
  if (pathname.includes('cash-flow')) return 'Cash Flow';
  if (pathname.includes('trial-balance')) return 'Trial Balance';

  return 'Back';
}

export default useNavigationContext;
